export const SYSTEM_USER_OWNER_FIELDS = [
  { key: "createdBy", labelKey: "CreatedBy" },
  { key: "updatedBy", labelKey: "UpdatedBy" },
];

function normalizeRef(value) {
  return String(value || "").trim().toLowerCase();
}

export function isUserReferenceField(field) {
  if (!field) return false;
  const type = normalizeRef(field.type);
  const ref = normalizeRef(field.ref);
  if (type === "users") return true;
  const isUserRef = ref === "user" || ref === "users";
  if (type === "reference" && isUserRef) return true;
  if (
    (type === "array" || field.isArray) &&
    normalizeRef(field.itemType) === "reference"
  ) {
    return isUserRef;
  }
  return false;
}

/** Referencia a otro modelo (no a usuarios) por la que se puede saltar un nivel. */
export function isNavigableReferenceField(field) {
  if (!field || !field.ref) return false;
  if (isUserReferenceField(field)) return false;
  const type = normalizeRef(field.type);
  if (type === "reference") return true;
  if (
    (type === "array" || field.isArray) &&
    normalizeRef(field.itemType) === "reference"
  ) {
    return true;
  }
  return false;
}

/** "proform.operational_in_charge" -> partes; un solo nivel de anidamiento. */
export function splitUserOwnerFieldKey(key) {
  const raw = key ? String(key).trim() : "";
  const dot = raw.indexOf(".");
  if (dot <= 0) return null;
  const referenceKey = raw.slice(0, dot).trim();
  const subFieldKey = raw.slice(dot + 1).trim();
  if (!referenceKey || !subFieldKey || subFieldKey.includes(".")) return null;
  return { referenceKey, subFieldKey };
}

export function isSystemUserOwnerField(key) {
  return key === "createdBy" || key === "updatedBy";
}

function fieldLabel(field) {
  if (!field) return "";
  return field.label || field.key;
}

/**
 * Opciones del selector: campos de usuario propios del modelo, los de sistema y
 * —un nivel más abajo— los campos de usuario de cada modelo referenciado.
 * `modelsByName` es un objeto/Map { modelName: definición } con las demás definiciones.
 */
export function collectUserOwnerFieldOptions(
  modelFields = [],
  modelsByName = null
) {
  const seen = new Set();
  const options = [];
  (modelFields || []).forEach((field) => {
    if (!field || !field.key || !isUserReferenceField(field)) return;
    if (seen.has(field.key)) return;
    seen.add(field.key);
    options.push({
      key: field.key,
      label: fieldLabel(field),
      isArray: !!field.isArray,
      isSystem: false,
      group: null,
    });
  });
  SYSTEM_USER_OWNER_FIELDS.forEach((sys) => {
    if (seen.has(sys.key)) return;
    seen.add(sys.key);
    options.push({
      key: sys.key,
      labelKey: sys.labelKey,
      isArray: false,
      isSystem: true,
      group: null,
    });
  });

  const getModel = (name) => {
    if (!name || !modelsByName) return null;
    if (typeof modelsByName.get === "function") return modelsByName.get(name);
    return modelsByName[name] || null;
  };

  (modelFields || []).forEach((field) => {
    if (!field || !field.key || !isNavigableReferenceField(field)) return;
    const refModel = getModel(field.ref);
    const refFields = Array.isArray(refModel?.fields) ? refModel.fields : [];
    refFields.forEach((subField) => {
      if (!subField || !subField.key || !isUserReferenceField(subField)) return;
      const key = `${field.key}.${subField.key}`;
      if (seen.has(key)) return;
      seen.add(key);
      options.push({
        key,
        label: fieldLabel(subField),
        isArray: !!subField.isArray,
        isSystem: false,
        group: fieldLabel(field),
        referenceKey: field.key,
        subFieldKey: subField.key,
      });
    });
  });

  return options;
}

export function isValidUserOwnerField(modelFields, fieldKey) {
  const key = fieldKey ? String(fieldKey).trim() : "";
  if (!key) return false;
  if (isSystemUserOwnerField(key)) return true;
  const nested = splitUserOwnerFieldKey(key);
  if (nested) {
    const refField = (modelFields || []).find(
      (f) => f && f.key === nested.referenceKey
    );
    return isNavigableReferenceField(refField);
  }
  const field = (modelFields || []).find((f) => f && f.key === key);
  return isUserReferenceField(field);
}

export function resolveUserOwnerFieldKeysFromModel(model) {
  if (!model) return [];
  const raw = [];
  if (Array.isArray(model.userOwnerFields)) {
    raw.push(...model.userOwnerFields);
  }
  if (model.userOwnerField) {
    raw.push(model.userOwnerField);
  }
  const seen = new Set();
  const keys = [];
  raw.forEach((item) => {
    const key = item ? String(item).trim() : "";
    if (!key || seen.has(key)) return;
    seen.add(key);
    keys.push(key);
  });
  return keys;
}

export function resolveValidUserOwnerFieldKeys(model, modelFields) {
  return resolveUserOwnerFieldKeysFromModel(model).filter((key) =>
    isValidUserOwnerField(modelFields, key)
  );
}
